<script setup lang="ts">
import { computed, onMounted, ref } from 'vue';
import EditPageLayout from './EditPageLayout.vue';
import { i18n } from '@/i18n';
import EzTable from '@/corePagesModule/components/ezTable/EzTable.vue';
import EzTableRow from '@/corePagesModule/components/ezTable/EzTableRow.vue';
import EzHeader from '@/corePagesModule/components/ezTable/EzHeader.vue';
import EzCell from '@/corePagesModule/components/ezTable/EzCell.vue';
import { MODE_CONFIG, TargetMasterItems } from '../constants';
import { useBulkEditBusinessStore } from './useEditBusinessStore/index';
import { storeToRefs } from 'pinia';
import { useTableHeader } from '@/corePagesModule/composables/useTableHeader';
import { useColumnResizer } from '@/corePagesModule/composables/useColumnResizer';
import { HeaderList } from '@/coreDomainsModule/table';
import { EditBusinessViewModel } from './editBusinessViewModel';
import SingleSelectInput from '@/corePagesModule/SelectInput/SingleSelectInput.vue';
import { EditMasterItem, initMasterItem } from './useEditBusinessStore/types';
import TextInput from '@/unmanagedBusinessModule/components/TextInput.vue';
import { MasterPairResolverService } from '@/corePagesModule/service/masterPairResolverService';
import TableHeaderModifyButton from '@/corePagesModule/components/TableHeaderModifyButton.vue';
import TextButton from '@/components/input/Btn/TextButton.vue';
import { showInfoModal } from '@/corePagesModule/components/InfoModal/InfoModalManager';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { diContainer } from '@/coreContainer/container';
import { waitLoading } from '@/corePagesModule/components/InfoModal/waitLoading';
import { toast } from '@/corePagesModule/components/Toast/ToastManager';
import { useColumnWidth } from '@/corePagesModule/composables/useColumnWidth';

const t = i18n.t;
const vm = new EditBusinessViewModel();
const store = useBulkEditBusinessStore();
const { state } = storeToRefs(store);
const { $reset, fetchEditBusiness } = store;

const tableRef = ref<HTMLElement | null>(null);
const tableWidthInfo = ref<Record<string, number | undefined>>({});
const selectedColumn = ref<string>('');
const targetMaster = ref<EditMasterItem>(initMasterItem());
const editedMaster = ref<EditMasterItem>(initMasterItem());
const isManualPairSelectionRequired = ref(false);
const pairMissingNotified = ref(false);
const targetSelectionRequestId = ref(0);

const pageTitle = computed(() => `${t('選択肢編集')}`);
const modeConfig = computed(() => MODE_CONFIG[state.value.businessMode]);
const tableType = computed(() => modeConfig.value.tableType.master);

const businessFreeColumnHeaderOptions = computed(() => {
  const headers = new Map<number, string>();
  for (const business of state.value.dataList) {
    for (const column of business.businessFreeColumnValues ?? []) {
      if (column.isConfigured) headers.set(column.columnNo, column.headerName);
    }
  }
  return [...headers.entries()]
    .sort(([a], [b]) => a - b)
    .map(([columnNo, headerName]) => ({ key: `businessFreeColumnValues-${columnNo}`, label: headerName, type: 'str' as const }));
});
const filteredFullHeaderOptions = computed(() => [
  ...modeConfig.value.headerOptions.filter((h) => h.key !== 'checkbox'),
  ...businessFreeColumnHeaderOptions.value,
]);
const fullHeaderKeys = computed(() => filteredFullHeaderOptions.value.map((h) => h.key));

const { headerList, updateDisplayHeader } = useTableHeader(fullHeaderKeys, tableType, []);
useColumnWidth({ tableType: tableType, widthInfo: tableWidthInfo });
const { onMousedownResizer } = useColumnResizer({
  tableType: tableType,
  widthInfo: tableWidthInfo,
  tableEl: tableRef,
});

const headerOptions = computed(() => filteredFullHeaderOptions.value.filter((h) => headerList.value.includes(h.key)));
const headerWidths = computed(() => headerOptions.value.map((h) => `${vm.getHeaderWidth(h, tableWidthInfo.value)}px`));
const ModifiableHeaderKeys = computed(
  (): HeaderList =>
    modeConfig.value.headerOptions
      .filter((h) => !['headerModify'].includes(h.key))
      .map((h) => ({
        key: h.key,
        text: h.label,
        isShown: headerList.value.includes(h.key),
      }))
);

const targetMasterFilterType = computed(
  (): EditMasterItem => ({
    code: `${toUpperSnakeCase(selectedColumn.value)}_CODE`,
    name: `${toUpperSnakeCase(selectedColumn.value)}${selectedColumn.value === 'warehouse' ? '' : '_NAME'}`,
  })
);

const canShowEditInputs = computed(
  () => targetMaster.value.code !== '' && targetMaster.value.name !== '' && state.value.loadingStatus.type === 'success'
);
const isEditedMasterXor = computed(() => {
  const isCodeChanged = editedMaster.value.code !== targetMaster.value.code;
  const isNameChanged = editedMaster.value.name !== targetMaster.value.name;
  return isCodeChanged !== isNameChanged;
});
const isBlank = computed(() => editedMaster.value.code === '' || editedMaster.value.name === '');
const isCommitDisable = computed(() => !canShowEditInputs.value || !isEditedMasterXor.value || isBlank.value);

/**
 * camelCaseをSelectInput用にUPPER_SNAKE_CASEに変換する
 * @param value - camelCaseの値
 */
const toUpperSnakeCase = (value: string): string => value.replace(/([a-z0-9])([A-Z])/g, '$1_$2').toUpperCase();

/**
 * マスタのペア取得に失敗した際にモーダルを表示してフラグを立てる
 */
const showPairMissingModal = async () => {
  if (pairMissingNotified.value) return;
  pairMissingNotified.value = true;

  await showInfoModal({
    modalTitle: t('対応する選択肢が取得できません'),
    modalDescription: t('コードと名称をそれぞれ選択してください。選択した組み合わせに一致する取引を表示します。'),
    icon: 'error',
    colorStyle: 'error',
    backButtonLabel: t('OK'),
    isDisabledNextButton: true,
  });
};

/**
 * ターゲット選択中に古い一覧や変更後入力が一瞬表示されないようにする
 */
const clearFetchedEditBusiness = () => {
  state.value.filterItems = [];
  state.value.dataList = [];
  state.value.checkedIds = new Set();
  state.value.loadingStatus.type = 'loading';
  editedMaster.value = initMasterItem();
};

/**
 * 手動ペア選択モードを解除する
 */
const resetManualPairSelection = () => {
  isManualPairSelectionRequired.value = false;
  pairMissingNotified.value = false;
};

/**
 * 編集対象カラムの選択を解除する
 */
const clearColumnSelection = () => {
  targetSelectionRequestId.value++;
  selectedColumn.value = '';
  targetMaster.value = initMasterItem();
  editedMaster.value = initMasterItem();
  resetManualPairSelection();
  clearFetchedEditBusiness();
};

/**
 * 編集対象カラムをセットする
 * @param value - 値
 */
const setColumn = (value: string) => {
  if (value === '') {
    clearColumnSelection();
    return;
  }

  for (const item of TargetMasterItems) {
    if (value === t(`editMaster:${item}`) && selectedColumn.value !== item) {
      targetSelectionRequestId.value++;
      selectedColumn.value = item;
      targetMaster.value = initMasterItem();
      editedMaster.value = initMasterItem();
      resetManualPairSelection();
      clearFetchedEditBusiness();
      state.value.businessMode = ['customer', 'salesDestination'].includes(item) ? 'baseBusiness' : 'aggregateBusiness';
      return;
    }
  }
};

/**
 * ターゲット情報をセットする
 * @param type - タイプ
 * @param value - 値
 */
const setTargetMasterValue = async (type: 'code' | 'name', value: string) => {
  const requestId = ++targetSelectionRequestId.value;
  targetMaster.value[type] = value;
  clearFetchedEditBusiness();

  if (value === '') {
    targetMaster.value = initMasterItem();
    resetManualPairSelection();
    return;
  }

  const anotherType = type === 'code' ? 'name' : 'code';
  const previousAnotherValue = targetMaster.value[anotherType];
  targetMaster.value[anotherType] = t('読み込み中・・・');

  const resolverKey = `${selectedColumn.value}${type === 'code' ? 'Number' : 'Name'}`;
  const pair = await MasterPairResolverService.resolveMasterPairValue(resolverKey, value);
  if (requestId !== targetSelectionRequestId.value) return;

  if (pair?.value) {
    targetMaster.value[anotherType] = pair.value;
    resetManualPairSelection();
  } else {
    targetMaster.value[anotherType] = isManualPairSelectionRequired.value ? previousAnotherValue : '';
    if (!targetMaster.value[anotherType]) {
      isManualPairSelectionRequired.value = true;
      await showPairMissingModal();
    }
  }

  if (isManualPairSelectionRequired.value && (!targetMaster.value.code || !targetMaster.value.name)) {
    return;
  }
  await fetchBySelectedMasterPair(requestId);
};

/**
 * ターゲット情報に合わせてfetchする
 * @param requestId - 重複リクエストを防ぐためのID
 */
const fetchBySelectedMasterPair = async (requestId = targetSelectionRequestId.value) => {
  const filterItems: FilterContentItem[] = [];

  if (targetMaster.value.code) {
    filterItems.push({
      filterType: `${selectedColumn.value}Number`,
      selectedValue: [targetMaster.value.code],
      label: '',
    });
  }

  if (targetMaster.value.name) {
    filterItems.push({
      filterType: selectedColumn.value === 'warehouse' ? 'warehouse' : `${selectedColumn.value}Name`,
      selectedValue: [targetMaster.value.name],
      label: '',
    });
  }

  state.value.filterItems = filterItems;
  await fetchEditBusiness();
  if (requestId !== targetSelectionRequestId.value) return;

  editedMaster.value.code = targetMaster.value.code;
  editedMaster.value.name = targetMaster.value.name;
};

/**
 * 変更した値をセットする
 * @param type - タイプ
 * @param value - 値
 */
const setEditedMasterValue = (type: 'code' | 'name', value: string) => {
  editedMaster.value[type] = value;
};

/**
 * 編集した内容を確定する
 */
const commitChange = async () => {
  // バリデーション
  const result = await waitLoading(diContainer.updateMasterUseCase.execute(selectedColumn.value, targetMaster.value, editedMaster.value));
  switch (result?.status.type) {
    case 'success':
      toast.success(t('変更に成功しました。'));
      clearColumnSelection();
      return;
    case 'error':
    default:
      await showInfoModal({
        modalTitle: t('変更に失敗しました'),
        modalDescription:
          result?.status.error.code === 'RESOURCE_CONFLICT'
            ? t('変更後の選択肢が重複しています。')
            : t('エラーが発生しました。リロードして再度お試しください。'),
        colorStyle: 'error',
        icon: 'error',
        isDisabledNextButton: true,
        backButtonLabel: t('OK'),
      });
      return;
  }
};

onMounted(async () => {
  $reset();
});
</script>
<template>
  <EditPageLayout :title="pageTitle">
    <div class="main">
      <div class="notice">
        <div class="desc">
          {{ t('選択肢のコードや名称を変更します。変更内容はその項目を使用しているすべての取引に反映されます。') }}
        </div>
        <div v-if="isManualPairSelectionRequired" class="warning">
          {{ t('対応する選択肢が取得できないため、コードと名称をそれぞれ選択してください。') }}
        </div>
      </div>
      <div class="header">
        <div class="header__inputs">
          <SingleSelectInput
            :label="t('編集対象')"
            :index="0"
            :placeholder="t('選択してください')"
            :model-value="selectedColumn ? t(`editMaster:${selectedColumn}`) : ''"
            filter-type="MASTER_ITEMS"
            :prop-options="TargetMasterItems.map((m) => t(`editMaster:${m}`))"
            @update:model-value="setColumn"
          />
          <SingleSelectInput
            v-if="!!selectedColumn"
            :key="`target-code-${targetMasterFilterType.code}`"
            :label="t('editMaster:コード')"
            :index="1"
            :placeholder="t('editMaster:コード')"
            :model-value="targetMaster.code"
            :filter-type="targetMasterFilterType.code"
            @update:model-value="(e) => setTargetMasterValue('code', e)"
          />
          <SingleSelectInput
            v-if="!!selectedColumn"
            :key="`target-name-${targetMasterFilterType.name}`"
            :label="t('editMaster:名称')"
            :index="2"
            :placeholder="t('editMaster:名称')"
            :model-value="targetMaster.name"
            :filter-type="targetMasterFilterType.name"
            @update:model-value="(e) => setTargetMasterValue('name', e)"
          />
          <span v-if="canShowEditInputs" class="header--arrow">→</span>
          <TextInput
            v-if="canShowEditInputs"
            :label="t('変更後のコード')"
            :model-value="editedMaster.code"
            :disabled="targetMaster.name !== editedMaster.name"
            @update:model-value="(e) => setEditedMasterValue('code', e)"
          />
          <TextInput
            v-if="canShowEditInputs"
            :label="t('変更後の名称')"
            :model-value="editedMaster.name"
            :disabled="targetMaster.code !== editedMaster.code"
            @update:model-value="(e) => setEditedMasterValue('name', e)"
          />
        </div>
        <TextButton class="header__button" :disabled="isCommitDisable" @click="commitChange">
          {{ t('editMaster:更新する') }}
        </TextButton>
      </div>
      <div v-if="targetMaster.name === ''" class="empty-state">
        <div class="empty-state__title">{{ t('編集する選択肢を選択してください') }}</div>
        <div class="empty-state__description">
          {{ t('編集対象と現在のコード・名称を選択すると、該当する取引が一覧に表示されます。') }}
        </div>
      </div>
      <div v-else class="table-container">
        <EzTable ref="tableRef" :grid-template-columns="headerWidths" :loading-status="state.loadingStatus" class="list-table">
          <template #header>
            <template v-for="header in headerOptions" :key="`header-${header.key}`">
              <EzHeader v-if="header.key === 'headerModify'" sticky="right" class="list-table__header">
                <TableHeaderModifyButton :modifiable-header-list="ModifiableHeaderKeys" @toggle="updateDisplayHeader" />
              </EzHeader>
              <EzHeader v-else use-resizer class="list-table__header" @resizer="(e) => onMousedownResizer(e, header.key)">
                {{ t(header.label) }}
              </EzHeader>
            </template>
          </template>
          <template #body>
            <EzTableRow v-for="(data, index1) in state.dataList" :key="`row-${index1}`" :grid-template-columns="headerWidths">
              <template v-for="header in headerOptions" :key="`row-${index1}-${header.key}`">
                <EzCell v-if="header.key === 'headerModify'" :line-height="24" class="list-table__cell" />
                <EzCell v-else-if="header.key.includes(selectedColumn)" :line-height="24" class="list-table__cell list-table__cell--edited">
                  {{ vm.getDisplayData(header.key, data) }}
                  <template
                    v-if="
                      editedMaster[header.key.includes('Number') ? 'code' : 'name'] !==
                      targetMaster[header.key.includes('Number') ? 'code' : 'name']
                    "
                  >
                    →{{ header.key.includes('Number') ? editedMaster.code : editedMaster.name }}
                  </template>
                </EzCell>
                <EzCell v-else :is-number="header.type === 'num'" :line-height="24" class="list-table__cell">
                  {{ vm.getDisplayData(header.key, data) }}
                </EzCell>
              </template>
            </EzTableRow>
          </template>
        </EzTable>
      </div>
    </div>
  </EditPageLayout>
</template>
<style lang="scss" scoped>
.desc {
  font-size: 15px;
}

.notice {
  display: grid;
  gap: 8px;
}

.main {
  padding-top: 8px;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
  gap: 8px;
  height: calc(100dvh - 120px);
  *:not(.material-icons) {
    font-family: 'Noto Sans JP';
  }
}

.warning {
  font-size: 14px;
  color: $semanticError;
}

.header {
  display: grid;
  grid-template-columns: 1fr max-content;
  align-items: end;
  &__inputs {
    display: grid;
    grid-template-columns: repeat(3, minmax(300px, max-content)) max-content repeat(2, minmax(300px, max-content));
    align-items: center;
    gap: 8px;
  }

  &--arrow {
    padding-top: 32px;
  }
}

.table-container {
  min-height: 0;
  overflow: hidden;
  background-color: $semanticWhite;
}

.empty-state {
  display: grid;
  place-content: center;
  gap: 8px;
  min-height: 0;
  padding: 40px 24px;
  text-align: center;
  background-color: $semanticWhite;
  border: 1px dashed $borderDefault;

  &__title {
    font-weight: 700;
    font-size: 16px;
  }

  &__description {
    font-size: 14px;
    color: $textSecondary;
  }
}

.list-table {
  position: relative;
  height: 100%;
  overflow-x: auto;
  background-color: $semanticWhite;
  &__header {
    height: 36px;
    font-weight: 700;
    font-size: 14px;
    line-height: 1;
  }
  &__row {
    &--all-select {
      color: $semanticPrimary;
      border-bottom: 1px solid $borderActive;
    }
  }
  &__cell {
    height: 24px;
    font-weight: 400;
    font-size: 12px;
    &--all-select {
      grid-column: 2 / -1;
    }
    &--edited {
      background-color: $semanticPrimarySubdued;
    }
  }
}
</style>
